import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      await refresh();
      navigate("/spazio");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async () => {
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If the account exists, you will receive an email with instructions.");
      setForgot(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center px-5">
      <div className="max-w-md w-full bg-card border border-border rounded-3xl p-10 animate-fade-in-up">
        <button onClick={() => navigate("/")} className="font-heading text-2xl uppercase tracking-[0.18em] mb-8 block">Self Doubling<span className="text-primary">©</span></button>
        <h1 className="font-heading text-3xl tracking-tight mb-3">{forgot ? "Recover your access" : "Sign in to the space"}</h1>
        <p className="text-muted-foreground mb-8">{forgot ? "We'll send you a link to reset your password." : "Welcome back."}</p>
        <div className="space-y-5">
          <div>
            <Label>Email</Label>
            <Input data-testid="login-email-input" type="email" className="mt-2 rounded-xl bg-background" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          {!forgot && (
            <div>
              <Label>Password</Label>
              <Input data-testid="login-password-input" type="password" className="mt-2 rounded-xl bg-background" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
            </div>
          )}
          {forgot ? (
            <Button className="btn-gold w-full rounded-full h-12 text-sm font-medium" disabled={loading} onClick={sendReset} data-testid="send-reset-btn">
              {loading ? "Sending…" : "Send link"}
            </Button>
          ) : (
            <Button className="btn-gold w-full rounded-full h-12 text-sm font-medium" disabled={loading} onClick={submit} data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          )}
          <div className="flex justify-between text-sm text-muted-foreground">
            <button onClick={() => setForgot(!forgot)} className="hover:text-foreground transition-colors" data-testid="toggle-forgot-btn">
              {forgot ? "← Back to sign in" : "Forgot password?"}
            </button>
            <button onClick={() => navigate("/request-access")} className="hover:text-foreground transition-colors" data-testid="to-request-btn">
              Request access
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
